import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  NavigateBefore,
  NavigateNext,
} from '@material-ui/icons';
import WelcomeStep from './steps/step-1-welcome';
import ExperienceSelectionStep from './steps/step-2-experience-selection';
import ConfigurationStep from './steps/step-3-configuration';
import ThemeStep from './steps/step-4-theme';
import SummaryStep from './steps/step-5-summary';
import CloudSummaryStep from './steps/step-5-summary/CloudSummaryStep';
import WizardProgress from '../wizard/components/WizardProgress';
import AnimatedStepSwitcher from '../../components/common/AnimatedStepSwitcher';
import { enqueueErrorSnackbar } from '../../actions/Notifications';
import { createWizardDraftTransport } from './wizardDraftTransport';
import WIZARD_CONFIG from './config/wizardConfig';
import { useWizardResume } from './hooks/useWizardResume';
import { useShellAppContext } from '../shell/context/useShellAppContext';
import { setWizardBypass } from '../shell/session/shellSessionStorage';
import wizardContainerStyles from '../wizard/components/WizardContainer.module.scss';
import { telemetryClient } from '../telemetry/telemetryClient';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { ReactComponent as Logo } from '../../assets/images/kealogo-text-bg.svg';

const SEARCH_SETUP_STEP_KEYS = {
  'experience-selection': 'wizard.searchSetup.step.welcome',
  'choose-experience': 'wizard.searchSetup.step.chooseExperience',
  'component-configuration': 'wizard.searchSetup.step.configuration',
  'theme-configuration': 'wizard.searchSetup.step.theme',
  summary: 'wizard.searchSetup.step.summary',
};

const STEP_COMPONENTS = {
  'experience-selection': WelcomeStep,
  'choose-experience': ExperienceSelectionStep,
  'component-configuration': ConfigurationStep,
  'theme-configuration': ThemeStep,
  summary: SummaryStep,
};

function IntegrationWizardNavigation({
  canGoPrev,
  canGoNext,
  isLastStep,
  loading,
  onPrev,
  onNext,
  localizeMessage,
}) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      className={wizardContainerStyles.navigation}
    >
      <Button
        variant="outlined"
        startIcon={<NavigateBefore />}
        onClick={onPrev}
        disabled={!canGoPrev || loading}
      >
        {localizeMessage('wizard.searchSetup.navigation.back')}
      </Button>

      <Box id="wizard-navigation-center" />

      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        {!isLastStep ? (
          <Button
            variant="contained"
            color="primary"
            endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <NavigateNext />}
            onClick={onNext}
            disabled={!canGoNext || loading}
          >
            {localizeMessage('wizard.searchSetup.navigation.next')}
          </Button>
        ) : <Box />}
      </Box>
    </Box>
  );
}

IntegrationWizardNavigation.propTypes = {
  canGoPrev: PropTypes.bool.isRequired,
  canGoNext: PropTypes.bool.isRequired,
  isLastStep: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  localizeMessage: PropTypes.func.isRequired,
};

IntegrationWizardNavigation.defaultProps = {
  loading: false,
};

/**
 * Shared first-setup flow for plugin and cloud products.
 *
 * Step progression is local UI state. Environment-specific persistence,
 * preview and publication are supplied by the shell runtime and the final
 * product-specific summary step.
 */
const IntegrationWizard = ({ onComplete }) => {
  const dispatch = useDispatch();
  const currentShop = useSelector((state) => state.app?.currentShop);
  const history = useHistory();
  const localizeMessage = useLocalizedMessage();
  const localizeMessageRef = useRef(localizeMessage);
  localizeMessageRef.current = localizeMessage;

  // Read shell-provided bridge client so useWizardResume reuses the single
  // BridgeClient instance created by ShellAppContextProvider instead of
  // creating a second one. Falls back gracefully to null when running outside
  // the shell (tests, legacy embed) — useWizardResume creates its own then.
  const {
    bridgeClient: shellBridgeClient,
    gatewayClient: shellGatewayClient,
    searchSettingsClient,
    productProfile,
    runtime,
  } = useShellAppContext();

  const {
    isLoading: resumeLoading,
    isCompleted,
    initialStepIndex,
    initialWizardData,
  } = useWizardResume(shellBridgeClient, { enabled: productProfile?.id !== 'cloud' });

  const [wizardData, setWizardData] = useState({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [readyMap, setReadyMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [resumeApplied, setResumeApplied] = useState(false);
  const stepSaveRef = useRef(null);
  const embeddedStepRef = useRef(null);
  const searchExperienceDraftRef = useRef(null);
  const wizardViewedRef = useRef(false);

  const activeSteps = useMemo(
    () => WIZARD_CONFIG.getActiveSteps(wizardData),
    [wizardData],
  );
  const progressSteps = activeSteps.map(step => ({
    ...step,
    title: localizeMessage(SEARCH_SETUP_STEP_KEYS[step.id]),
  }));
  const isCloudProduct = productProfile?.id === 'cloud';

  const boundedStepIndex = Math.min(
    currentStepIndex,
    Math.max(activeSteps.length - 1, 0),
  );
  const currentStep = activeSteps[boundedStepIndex] || null;
  const StepComponent = currentStep?.id === 'summary' && productProfile?.id === 'cloud'
    ? CloudSummaryStep
    : (currentStep ? STEP_COMPONENTS[currentStep.id] : null);
  const isLastStep = boundedStepIndex === activeSteps.length - 1;
  const isStepReady = readyMap[boundedStepIndex] !== false;
  const canGoNext = !!currentStep
    && WIZARD_CONFIG.canProceed(boundedStepIndex, wizardData)
    && isStepReady
    && !isSaving;
  const canGoPrev = boundedStepIndex > 0 && !isSaving;

  useEffect(() => {
    if (boundedStepIndex !== currentStepIndex) {
      setCurrentStepIndex(boundedStepIndex);
    }
  }, [boundedStepIndex, currentStepIndex]);

  useEffect(() => {
    if (!resumeLoading && !resumeApplied) {
      setCurrentStepIndex(initialStepIndex);
      if (Object.keys(initialWizardData).length > 0) {
        setWizardData(initialWizardData);
      }
      setResumeApplied(true);
    }
  }, [resumeLoading, resumeApplied, initialStepIndex, initialWizardData]);

  useEffect(() => {
    if (!resumeApplied || !currentStep) return;
    if (!wizardViewedRef.current) {
      telemetryClient.capture(initialStepIndex > 0 ? 'wizard_reopened' : 'wizard_viewed', {
        resumed: initialStepIndex > 0,
        step_id: currentStep.id,
        step_index: boundedStepIndex,
      });
      wizardViewedRef.current = true;
    }
    telemetryClient.capture('wizard_step_viewed', {
      resumed: initialStepIndex > 0,
      step_id: currentStep.id,
      step_index: boundedStepIndex,
    });
  }, [boundedStepIndex, currentStep, initialStepIndex, resumeApplied]);

  useEffect(() => {
    if (!isCompleted) return;
    if (onComplete) {
      onComplete();
    }
    // Note: previously we auto-redirected to /home when isCompleted. That made
    // the "Open wizard" button on SetupWelcome a no-op (round-trip home → setup
    // → home) and trapped users who wanted to revisit a completed wizard.
    // We now render a "Wizard already complete" surface instead of redirecting,
    // so the user can return to home or restart the wizard at will.
  }, [isCompleted, onComplete]);

  // Local override: allows the user to re-enter the wizard even when the
  // plugin reports wizard_completed=true. Cleared when the component unmounts
  // or the user navigates away.
  const [forceReopen, setForceReopen] = useState(false);
  const effectiveIsCompleted = isCompleted && !forceReopen;

  const handleStepUpdate = useCallback((newData) => {
    setWizardData((currentData) => {
      const nextData = {
        ...currentData,
        ...newData,
      };

      if (
        Object.prototype.hasOwnProperty.call(newData, 'selectedExperience')
        && newData.selectedExperience !== currentData.selectedExperience
      ) {
        telemetryClient.capture('wizard_experience_selected', {
          experience: newData.selectedExperience,
          step_id: currentStep?.id || 'choose-experience',
          step_index: boundedStepIndex,
        });
        searchExperienceDraftRef.current = null;
      }

      return nextData;
    });
  }, [boundedStepIndex, currentStep]);

  const handleStepReady = useCallback((isReady) => {
    setReadyMap((currentReadyMap) => ({
      ...currentReadyMap,
      [boundedStepIndex]: isReady,
    }));
  }, [boundedStepIndex]);

  const handleRegisterSave = useCallback((saveFn) => {
    stepSaveRef.current = saveFn;
  }, []);

  // Called by EmbeddedWizard when its last sub-step completes.
  const handleEmbeddedComplete = useCallback(() => {
    setCurrentStepIndex((stepIndex) => Math.min(stepIndex + 1, activeSteps.length - 1));
  }, [activeSteps.length]);

  const executeSave = useCallback(async () => {
    if (!stepSaveRef.current) {
      return;
    }

    setIsSaving(true);
    try {
      await stepSaveRef.current();
    } catch (error) {
      dispatch(enqueueErrorSnackbar(
        error.message || localizeMessage('wizard.searchSetup.saveFailed'),
      ));
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, localizeMessage]);

  const handleNext = useCallback(async () => {
    if (!currentStep) {
      return;
    }
    telemetryClient.capture('wizard_next_clicked', {
      step_id: currentStep.id,
      step_index: boundedStepIndex,
    });

    // For the embedded configuration step, delegate Next to the embedded wizard
    // so it can advance its own sub-steps. When the embedded wizard finishes its
    // last sub-step it calls onComplete -> handleEmbeddedComplete -> outer advances.
    if (currentStep.id === 'component-configuration' && embeddedStepRef.current?.handleNext) {
      embeddedStepRef.current.handleNext();
      return;
    }

    try {
      await executeSave();
    } catch {
      telemetryClient.capture('wizard_step_validation_blocked', {
        step_id: currentStep.id,
        step_index: boundedStepIndex,
        normalized_reason_code: 'step_save_failed',
      });
      return;
    }

    if (!isLastStep) {
      setCurrentStepIndex((stepIndex) => Math.min(stepIndex + 1, activeSteps.length - 1));
      return;
    }

    if (onComplete) {
      onComplete();
    }
  }, [activeSteps.length, boundedStepIndex, currentStep, executeSave, isLastStep, onComplete]);

  const handlePrev = useCallback(() => {
    if (currentStep) {
      telemetryClient.capture('wizard_back_clicked', {
        step_id: currentStep.id,
        step_index: boundedStepIndex,
      });
    }
    setCurrentStepIndex((stepIndex) => Math.max(stepIndex - 1, 0));
  }, [boundedStepIndex, currentStep]);

  const handleStepClick = useCallback((stepIndex) => {
    if (stepIndex <= boundedStepIndex) {
      setCurrentStepIndex(stepIndex);
    }
  }, [boundedStepIndex]);

  // Plan B: "Skip for now" — navigate to Home without completing the wizard.
  // Sets a bypass flag in the shell session so the wizard gate doesn't loop.
  // history is stable (react-router guarantee) so empty deps array is intentional.
  const handleSkip = useCallback(() => {
    telemetryClient.capture('wizard_skipped', {
      step_id: currentStep?.id || 'unknown',
      step_index: boundedStepIndex,
    });
    setWizardBypass();
    history.replace(
      history.location.pathname.replace(/\/(setup|integration-wizard)(\/.*)?$/, '/home'),
    );
  }, [boundedStepIndex, currentStep, history]);

  const wizardTransport = useMemo(() => createWizardDraftTransport({
    selectedExperience: wizardData.selectedExperience,
    draftConfig: searchExperienceDraftRef.current,
    searchSettingsClient: searchSettingsClient || shellGatewayClient,
    localizeMessage: (id, values) => localizeMessageRef.current(id, values),
    onDraftChange: (nextConfig) => {
      searchExperienceDraftRef.current = nextConfig;
    },
  }), [searchSettingsClient, shellGatewayClient, wizardData.selectedExperience]);

  if (!currentShop) {
    return (
      <Box className={wizardContainerStyles.loading}>
        <Alert severity="warning">
          {localizeMessage('wizard.searchSetup.shopRequired')}
        </Alert>
      </Box>
    );
  }

  if (resumeLoading || !resumeApplied || !currentStep || !StepComponent) {
    return (
      <Box className={wizardContainerStyles.loading}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Wizard already completed on the plugin side, and the user has not
  // explicitly asked to reopen it. Render a friendly "already complete"
  // surface with explicit "Go to home" and "Restart wizard" actions.
  // This replaces the previous auto-redirect, which trapped users on the
  // Home screen when they wanted to revisit a finished wizard.
  if (effectiveIsCompleted) {
    return (
      <Box
        className={`${wizardContainerStyles.root} ${isCloudProduct ? wizardContainerStyles.cloudRoot : ''}`}
        data-testid="wizard-already-complete"
      >
        <Paper className={wizardContainerStyles.paper}>
          <Box p={4} display="flex" flexDirection="column" alignItems="center" style={{ gap: 16 }}>
            <Alert severity="success" data-testid="wizard-already-complete-alert">
              {localizeMessage('wizard.searchSetup.complete.description')}
            </Alert>
            <Box display="flex" style={{ gap: 12 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push(
                  history.location.pathname.replace(/\/(setup|integration-wizard)(\/.*)?$/, '/home'),
                )}
                data-testid="wizard-go-home-btn"
              >
                {localizeMessage('wizard.searchSetup.complete.home')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setForceReopen(true)}
                data-testid="wizard-restart-btn"
              >
                {localizeMessage('wizard.searchSetup.complete.reopen')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  const stepProps = {
    data: wizardData,
    onUpdate: handleStepUpdate,
    onReady: handleStepReady,
    onRegisterSave: handleRegisterSave,
    shopId: currentShop,
    client: ['component-configuration', 'theme-configuration', 'summary'].includes(currentStep?.id)
      ? wizardTransport
      : undefined,
    onComplete: currentStep?.id === 'component-configuration'
      ? handleEmbeddedComplete
      : undefined,
    productId: productProfile?.id || null,
    runtime,
  };

  return (
    <Box
      className={`${wizardContainerStyles.root} ${isCloudProduct ? wizardContainerStyles.cloudRoot : ''}`}
      data-testid="search-experience-wizard-surface"
    >
      {isCloudProduct ? (
        <div className={wizardContainerStyles.cloudHeader}>
          <div className={wizardContainerStyles.cloudBrand} aria-label="Kea Labs">
            <Logo />
          </div>
          <div className={wizardContainerStyles.cloudProgress}>
            <div className={wizardContainerStyles.flowLabel}>
              {localizeMessage('wizard.searchSetup.flowLabel')}
            </div>
            <WizardProgress
              steps={progressSteps}
              currentStep={boundedStepIndex}
              completedSteps={activeSteps.map((_, index) => index).filter((index) => index < boundedStepIndex)}
              onStepClick={handleStepClick}
              variant="cloud"
            />
          </div>
        </div>
      ) : (
        <WizardProgress
          steps={progressSteps}
          currentStep={boundedStepIndex}
          completedSteps={activeSteps.map((_, index) => index).filter((index) => index < boundedStepIndex)}
          onStepClick={handleStepClick}
        />
      )}

      <Paper className={wizardContainerStyles.paper}>
        <IntegrationWizardNavigation
          canGoPrev={canGoPrev}
          canGoNext={canGoNext}
          isLastStep={isLastStep}
          loading={isSaving}
          onPrev={handlePrev}
          onNext={handleNext}
          localizeMessage={localizeMessage}
        />

        <Box className={wizardContainerStyles.stepContent}>
          <AnimatedStepSwitcher stepKey={boundedStepIndex}>
            <StepComponent
              {...stepProps}
              {...(currentStep.id === 'component-configuration'
                ? { ref: embeddedStepRef }
                : {})}
            />
          </AnimatedStepSwitcher>
        </Box>

        <Box className={wizardContainerStyles.planBSkip}>
          <Button
            variant="text"
            color="default"
            size="small"
            onClick={handleSkip}
            data-testid="wizard-skip-btn"
          >
            {localizeMessage('wizard.searchSetup.navigation.skip')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

IntegrationWizard.propTypes = {
  onComplete: PropTypes.func,
};

IntegrationWizard.defaultProps = {
  onComplete: null,
};

export default IntegrationWizard;
